import * as React from 'react';
import { useEffect, useState } from 'react';
import { zVar } from '../../../ui/theme';
import { Card } from '../../../ui/Card';
import { useMediaQuery } from '../../../ui/useMediaQuery';

/*
 * TrickCard — one card played to the trick, with two bits of motion:
 *
 *   1. Slide-in: on mount it flies from the seat that played it (its `origin`)
 *      to its resting trick slot, so a play reads as the card leaving the hand.
 *   2. Sweep: once the trick is won, every card slides to the winner's seat and
 *      fades — the winner "sweeps" the pile before the server clears it.
 *
 * All motion is CSS transitions on transform/opacity (no keyframes): the card is
 * absolutely positioned at its resting slot, and we translate it relative to
 * that toward the origin (pre-entry) or the winner (sweep). Both are skipped
 * entirely when the player prefers reduced motion — the card simply appears in
 * its slot and disappears when the trick is swept.
 */

/** How much the trick-winning card is enlarged while the trick sits complete. */
const WINNER_SCALE = 1.08;

interface Point {
  x: number;
  y: number;
}

interface TrickCardProps {
  code: string;
  isWinner: boolean;
  /** Resting position: this seat's trick slot, in design px. */
  rest: Point;
  /** Where the card flies in from (the seat's hand), in design px. */
  origin: Point;
  /** Winner's seat position to sweep toward, in design px (set once won). */
  sweepTo?: Point;
  /** True once the completed trick should sweep to the winner. */
  sweeping: boolean;
  cardW: number;
  cardH: number;
}

// Transform that translates the card (centered on its slot) to a target point,
// at a given scale.
const to = (rest: Point, target: Point, scale: number): string =>
  `translate(calc(-50% + ${target.x - rest.x}px), calc(-50% + ${
    target.y - rest.y
  }px)) scale(${scale})`;

export const TrickCard: React.FC<TrickCardProps> = ({
  code,
  isWinner,
  rest,
  origin,
  sweepTo,
  sweeping,
  cardW,
  cardH,
}) => {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  // Start pre-entry (at the origin), then flip to entered on the next frame so
  // the transition runs from origin → rest.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const swept = sweeping && !!sweepTo;

  let transform: string;
  let opacity: number;
  let transition: string;
  if (reducedMotion) {
    transform = `translate(-50%, -50%) scale(${isWinner ? WINNER_SCALE : 1})`;
    opacity = swept ? 0 : 1;
    transition = 'none';
  } else if (swept) {
    transform = to(rest, sweepTo!, 0.35);
    opacity = 0;
    transition =
      'transform var(--dur-slow) var(--ease-out), opacity var(--dur-slow) var(--ease-out)';
  } else if (!entered) {
    transform = to(rest, origin, 0.9);
    opacity = 0.25;
    transition = 'none';
  } else {
    transform = `translate(-50%, -50%) scale(${isWinner ? WINNER_SCALE : 1})`;
    opacity = 1;
    transition = `transform var(--dur-base) var(--ease-out), opacity var(--dur-base) var(--ease-out)`;
  }

  return (
    <div
      style={{
        position: 'absolute',
        left: rest.x,
        top: rest.y,
        transform,
        opacity,
        transition,
        zIndex: zVar('z-trick'),
        pointerEvents: 'none',
      }}
    >
      <Card code={code} width={cardW} height={cardH} legal={isWinner && !swept} />
    </div>
  );
};

export default TrickCard;
